import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCircle, XCircle } from "lucide-react";
import { getIsClickingButton, setIsClickingButton } from "./inline-edit-controls";
import { isAutoSaveField } from "./editable-cell-permissions";
import { EditableCellEditInputs } from "./editable-cell-edit-inputs";
import {
  EDIT_CONTAINER_WRAPPER_CLASS,
  EDIT_CONTAINER_TEXTAREA_WRAPPER_CLASS,
  EDIT_CONTAINER_INLINE_WRAPPER_CLASS,
  EDIT_CONTENT_TEXTAREA_CLASS,
  EDIT_CONTENT_INLINE_CLASS,
  EDIT_BUTTONS_WRAPPER_CLASS,
  EDIT_BUTTONS_TEXTAREA_CLASS,
} from "./editable-cell-edit-constants";
import type {
  SimpleFieldConfig,
  FieldValue,
  BaseEntity,
  CellSaveHandler,
  CellCancelHandler,
  UniversalInlineEditHandler,
} from "../types/index";

export interface EditableCellEditContainerProps<T extends BaseEntity = BaseEntity> {
  value: FieldValue;
  fieldConfig: SimpleFieldConfig;
  rowId: string;
  fieldId: string;
  editValue: string;
  isTextareaField: boolean;
  data: T[];
  onSave: CellSaveHandler;
  onCancel: CellCancelHandler;
  setEditValue: (value: string) => void;
  onInlineEdit: UniversalInlineEditHandler;
}

/**
 * Renders a cell in edit mode
 * Handles layout, input rendering, and save/cancel buttons
 * Delegates input type dispatch to EditableCellEditInputs
 */
export function EditableCellEditContainer<T extends BaseEntity = BaseEntity>({
  value,
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  isTextareaField,
  data,
  onSave,
  onCancel,
  setEditValue,
  onInlineEdit,
}: EditableCellEditContainerProps<T>): React.ReactNode {
  // Create a unique key for this cell instance
  const cellKey = `${rowId}-${fieldId}`;

  return (
    <div
      className={cn(
        EDIT_CONTAINER_WRAPPER_CLASS,
        isTextareaField
          ? EDIT_CONTAINER_TEXTAREA_WRAPPER_CLASS
          : EDIT_CONTAINER_INLINE_WRAPPER_CLASS
      )}
    >
      <div
        className={cn(
          "w-full",
          isTextareaField ? EDIT_CONTENT_TEXTAREA_CLASS : EDIT_CONTENT_INLINE_CLASS
        )}
      >
        <EditableCellEditInputs<T>
          value={value}
          fieldConfig={fieldConfig}
          rowId={rowId}
          fieldId={fieldId}
          editValue={editValue}
          data={data}
          onSave={onSave}
          onCancel={onCancel}
          setEditValue={setEditValue}
          onInlineEdit={onInlineEdit}
          cellKey={cellKey}
        />
      </div>

      {/* Show save/cancel buttons only for manual-save fields */}
      {!isAutoSaveField(fieldConfig, fieldId) ? (
        <div
          className={cn(EDIT_BUTTONS_WRAPPER_CLASS, isTextareaField && EDIT_BUTTONS_TEXTAREA_CLASS)}
        >
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => {
              // Prevent blur event from firing when clicking save
              setIsClickingButton(cellKey, true);
              e.preventDefault();
            }}
            onClick={async () => {
              try {
                await onSave(rowId, fieldId);
              } catch (error) {
                console.error("Error saving field:", error);
                // Error is handled by onSave
              }
            }}
            className="h-7 w-7 p-0 hover:bg-green-100 text-green-600 hover:text-green-700 dark:hover:bg-green-900/40 dark:text-green-400 dark:hover:text-green-300 transition-all duration-200 hover:scale-105"
            title="Save changes (Enter or click away)"
          >
            <CheckCircle className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => {
              // Prevent blur event from firing when clicking cancel
              setIsClickingButton(cellKey, true);
              e.preventDefault();
            }}
            onClick={() => {
              onCancel();
            }}
            className="h-7 w-7 p-0 hover:bg-red-100 text-red-600 hover:text-red-700 dark:hover:bg-red-900/40 dark:text-red-400 dark:hover:text-red-300 transition-all duration-200 hover:scale-105"
            title="Cancel editing (Escape)"
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
